'use client';

import {
  FormEvent, useCallback, useEffect, useRef, useState,
} from 'react';
import { useTranslation } from 'next-i18next';
import { AdjustmentsHorizontalIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { ArrowsUpDownIcon } from '@heroicons/react/16/solid';
import { PromptBarShell } from '@components/features/candidate-prompt-bar/PromptBarShell';
import { PromptBarActionsPopup } from '@components/features/candidate-prompt-bar/PromptBarActionsPopup';
import {
  Button,
  ListboxStyled,
  ListboxStyledButton,
  ListboxStyledOption,
  ListboxStyledOptions,
} from '@components/common';
import { buildSearchParams } from '@utils/data-fetching';
import { useApplicantFilterDrawer } from '@components/contexts/ApplicantFilterDrawerContext';
import { sortBy } from '@components/features/filters/helpers/filterObjects';
import { filterStyles } from '@components/features/filters/helpers/filterStyles';
import { RadioCheckedIcon } from '@components/common/Icons';
import { useTalentSearchSuggestions } from '@hooks/filters/useTalentSearchSuggestions';
import { DocumentInterface } from '@type/v1-api-types';

interface TalentSearchPromptProps {
  locale: Locale;
  initialKeyword?: string;
  showFilterButton?: boolean;
  showSortButton?: boolean;
  sortValue?: string;
  onSortChange?: (sort: string) => void;
  onKeywordSubmit?: (keyword: string) => void;
  /** /my-jobs is auth-guarded, so the missions menu is built for an authenticated user. */
  isAuthenticated?: boolean;
  /** Overrides the talent-search placeholder (already translated). */
  placeholder?: string;
  /** Talent-keyword autocomplete + search-alert refresh; off for non-talent searches (my-jobs). */
  withSuggestions?: boolean;
}

/**
 * @description Fixed talent-search prompt bar (my-jobs pages + /cv/search). Search icon
 * only — no voice input. Typing opens an upward autocomplete panel with keyword
 * suggestions and the user's saved search-alert keywords (shared with the navbar
 * SearchSection via useTalentSearchSuggestions).
 */
export const TalentSearchPrompt = ({
  locale,
  initialKeyword = '',
  showFilterButton = false,
  showSortButton = false,
  sortValue = '',
  onSortChange,
  onKeywordSubmit,
  isAuthenticated = true,
  placeholder: placeholderProp,
  withSuggestions = true,
}: TalentSearchPromptProps) => {
  const { t } = useTranslation('common');
  const filterDrawer = useApplicantFilterDrawer();
  const [keyword, setKeyword] = useState(initialKeyword);
  const [isEditing, setIsEditing] = useState(false);
  // Clearing the input (native X of type="search", or select-all + delete) dismisses the
  // autocomplete until the user types again or re-expands the bar.
  const [suggestionsDismissed, setSuggestionsDismissed] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const { suggestions, fetchSuggestions, mutateSearchAlerts } = useTalentSearchSuggestions(locale);

  useEffect(() => {
    setKeyword(initialKeyword);
  }, [initialKeyword]);

  // Focus the real input (and drop the caret at the end) once expanded.
  useEffect(() => {
    if (!isEditing) return;
    const el = inputRef.current;
    if (!el) return;
    el.focus();
    const len = el.value.length;
    el.setSelectionRange(len, len);
  }, [isEditing]);

  const submitKeyword = useCallback((nextKeyword: string) => {
    const trimmedKeyword = nextKeyword.trim();

    // A submitted keyword may create/update a resume search alert server-side — refresh
    // the saved-alerts suggestion list shortly after (same delay as the navbar input).
    if (withSuggestions && trimmedKeyword) {
      setTimeout(() => {
        void mutateSearchAlerts();
      }, 1000);
    }

    if (onKeywordSubmit) {
      onKeywordSubmit(trimmedKeyword);
      return;
    }

    const search = buildSearchParams({ keyword: trimmedKeyword }, '?', true);
    // Hard navigation (full reload) to the pages-router talent-search page. A soft router.push from
    // this App Router page would be caught by the (.)cv/[slug] intercepting route — "search" matches
    // [slug], opening the CV-detail modal which 404s on /v2/user-resume-detail/search. The locale
    // prefix + full reload bypass the intercept. SearchSection.tsx uses the same workaround.
    window.location.href = `/${locale}/cv/search${search}`;
  }, [locale, onKeywordSubmit, mutateSearchAlerts, withSuggestions]);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    // Collapse the expanded input — matters when onKeywordSubmit filters in place
    // (my-jobs) instead of navigating away.
    setIsEditing(false);
    submitKeyword(keyword);
  };

  const chooseSuggestion = useCallback((suggestion: string) => {
    setKeyword(suggestion);
    setIsEditing(false);
    submitKeyword(suggestion);
  }, [submitKeyword]);

  const placeholder = placeholderProp ?? t('header.autocomplete.placeholder.talents');
  const selectedSort = sortBy.fields.find((field) => field.recordOptions?.defaultValue === sortValue)
    ?? sortBy.fields[0];

  const popup = <PromptBarActionsPopup locale={locale} mode="search" isAuthenticated={isAuthenticated} />;

  const suggestionItems = (suggestions ?? [])
    .map((item) => (item as DocumentInterface).suggestion)
    .filter((s): s is string => Boolean(s))
    .slice(0, 8);

  // Upward autocomplete panel. onMouseDown (not onClick) so the pick lands before the
  // input's blur collapses the expanded state and unmounts the panel.
  const suggestionsPanel = withSuggestions && isEditing && !suggestionsDismissed && suggestionItems.length > 0 && (
    <ul className="absolute bottom-full inset-x-0 z-10 mb-3 max-h-64 overflow-y-auto rounded-xl border border-input bg-surface p-1 shadow-lg">
      {suggestionItems.map((suggestion) => (
        <li key={suggestion}>
          <button
            type="button"
            onMouseDown={(event) => {
              event.preventDefault();
              chooseSuggestion(suggestion);
            }}
            className="flex w-full items-center rounded-lg px-3 py-2 text-left text-sm text-ink hover:bg-surface-100"
          >
            {suggestion}
          </button>
        </li>
      ))}
    </ul>
  );

  const actionButtons = (
    <div className="ml-auto flex items-center gap-2">
      {showFilterButton && filterDrawer && (
        <Button
          type="button"
          styling="outline"
          color="light"
          rounding="none"
          onClick={filterDrawer.toggleDrawer}
          aria-label={t('global.label.filter')}
          title={t('global.label.filter')}
          className="!h-10 !w-10 shrink-0 !rounded-lg !bg-transparent !p-0 !text-ink-medium hover:!bg-surface-100 hover:!text-ink-medium"
        >
          <AdjustmentsHorizontalIcon className="size-5" />
        </Button>
      )}
      {showSortButton && onSortChange && (
        <ListboxStyled
          className="relative shrink-0"
          by="label"
          value={selectedSort}
          onChange={(field) => {
            onSortChange(String(field.recordOptions?.defaultValue ?? ''));
          }}
        >
          <ListboxStyledButton
            type="button"
            aria-label={t('global.label.order-by')}
            title={t('global.label.order-by')}
            className="flex size-10 items-center justify-center rounded-lg border border-input bg-transparent p-0 text-ink-medium transition hover:bg-surface-100 hover:text-ink-medium"
          >
            <ArrowsUpDownIcon className="size-5" />
          </ListboxStyledButton>

          <ListboxStyledOptions
            className={`${filterStyles.listboxOptionsWrapper} pb-2`}
            animation="slideDown"
            anchor={{ to: 'top end', gap: '0.75rem' }}
          >
            <span className="my-2 block px-3.5 text-sm text-ink-light">{`${t('order-by')}:`}</span>
            {sortBy.fields.map((field) => (
              <ListboxStyledOption
                key={field.label}
                value={field}
                className={`${filterStyles.listboxOption} gap-1.5`}
              >
                <RadioCheckedIcon className={filterStyles.listboxOptionCheck} />
                {t(field.label)}
              </ListboxStyledOption>
            ))}
          </ListboxStyledOptions>
        </ListboxStyled>
      )}
      <Button
        type="submit"
        styling="outline"
        color="light"
        rounding="none"
        aria-label={t('header.search.button')}
        title={t('header.search.button')}
        className="!h-10 !w-10 shrink-0 !rounded-lg bg-secondary !p-0 transition-transform hover:scale-110 hover:!bg-secondary"
      >
        <MagnifyingGlassIcon className="size-5 text-white" />
      </Button>
    </div>
  );

  // Collapsed: single compact row. Expanded (typing): input grows onto its own line.
  if (!isEditing) {
    return (
      <PromptBarShell>
        <form onSubmit={handleSubmit} className="relative flex w-full items-center gap-2">
          {popup}
          <button
            type="button"
            onClick={() => {
              setSuggestionsDismissed(false);
              setIsEditing(true);
            }}
            aria-label={placeholder}
            className="flex min-w-0 flex-1 p-2 text-left text-sm"
          >
            {keyword
              ? <span className="block w-full truncate text-ink">{keyword}</span>
              : <span className="block w-full truncate text-ink/60">{placeholder}</span>}
          </button>
          {actionButtons}
        </form>
      </PromptBarShell>
    );
  }

  return (
    <PromptBarShell>
      <form onSubmit={handleSubmit} className="relative flex w-full flex-col gap-1">
        {suggestionsPanel}
        <input
          ref={inputRef}
          type="search"
          value={keyword}
          onChange={(event) => {
            const { value } = event.target;
            setKeyword(value);
            if (value.trim()) {
              setSuggestionsDismissed(false);
              if (withSuggestions) fetchSuggestions(value.trim());
            } else {
              // Emptied (native X clear or deletion) — hide the panel instead of falling
              // back to the saved-alerts list.
              setSuggestionsDismissed(true);
            }
          }}
          onBlur={() => setIsEditing(false)}
          placeholder={placeholder}
          aria-label={placeholder}
          className="w-full border-none bg-transparent p-2 text-sm text-ink shadow-none outline-none placeholder:text-ink/60 focus:ring-0 focus:ring-offset-0"
        />
        <div className="flex w-full items-center gap-2">
          {popup}
          {actionButtons}
        </div>
      </form>
    </PromptBarShell>
  );
};
